import { BedDouble, Camera, Car, MapPin, Sparkles, UtensilsCrossed } from "lucide-react";

const routeStops = (days) => days.reduce((acc, d) => {
  if (!d.overnight) return acc;
  const last = acc[acc.length - 1];
  if (last && last.place === d.overnight) last.nights += 1;
  else acc.push({ place: d.overnight, nights: 1 });
  return acc;
}, []);

const Detail = ({ icon: Icon, label, items, testId }) => {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="rounded-xl bg-mist/70 border border-sky-50 p-4" data-testid={testId}>
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-ocean"><Icon className="h-3.5 w-3.5" />{label}</p>
      <ul className="mt-2 space-y-1.5">
        {list.map((i) => <li key={i} className="flex gap-2 text-sm text-slate-700 leading-snug"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-aqua" />{i}</li>)}
      </ul>
    </div>
  );
};

const RouteOverview = ({ days }) => {
  const stops = routeStops(days);
  if (!stops.length) return null;
  return (
    <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="itinerary-route">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Route overview</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {stops.map((s, i) => (
          <span key={`${s.place}-${i}`} className="flex items-center gap-2">
            {i > 0 && <span className="h-px w-4 sm:w-6 bg-sky-200" />}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1.5 text-sm font-medium text-navy">
              <MapPin className="h-3.5 w-3.5 text-ocean" />{s.place}<span className="font-mono text-xs text-ocean">{s.nights}N</span>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};

const Day = ({ d }) => {
  const stay = d.stay && d.overnight ? `${d.stay}, ${d.overnight}` : d.overnight || (d.destination ? "Check-out · tour ends" : "");
  return (
    <li className="relative sm:pl-16" data-testid={`itinerary-day-${d.day}`}>
      <span className="hidden sm:grid absolute left-0 top-0 h-11 w-11 rounded-full bg-ocean text-white place-items-center font-mono text-sm font-semibold ring-4 ring-white">{d.day}</span>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 transition-shadow hover:shadow-md">
        <div className="flex flex-wrap items-center gap-2">
          <span className="sm:hidden h-7 w-7 rounded-full bg-ocean text-white grid place-items-center font-mono text-xs font-semibold">{d.day}</span>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-cyan-700">Day {d.day}</p>
          {d.destination && <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-ocean" data-testid={`itinerary-day-${d.day}-destination`}><MapPin className="h-3 w-3" />{d.destination}</span>}
        </div>
        <h3 className="mt-2 text-lg sm:text-xl font-semibold text-navy">{d.title}</h3>
        {d.description && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d.description}</p>}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Detail icon={Camera} label="Sightseeing" items={d.sightseeing} testId={`itinerary-day-${d.day}-sightseeing`} />
          <Detail icon={Sparkles} label="Activities" items={d.activities} testId={`itinerary-day-${d.day}-activities`} />
          <Detail icon={Car} label="Transfers" items={d.transfers} testId={`itinerary-day-${d.day}-transfers`} />
          <Detail icon={BedDouble} label="Hotel stay" items={[stay]} testId={`itinerary-day-${d.day}-stay`} />
        </div>
        {d.meals && <p className="mt-3 text-sm text-slate-600" data-testid={`itinerary-day-${d.day}-meals`}><UtensilsCrossed className="inline h-4 w-4 mr-1.5 -mt-0.5 text-ocean" /><span className="font-semibold text-navy">Meals:</span> {d.meals}</p>}
      </div>
    </li>
  );
};

export const ItineraryTimeline = ({ days }) => (
  <div data-testid="package-itinerary">
    <RouteOverview days={days} />
    <ol className="relative space-y-4 sm:space-y-6 sm:before:absolute sm:before:left-[21px] sm:before:top-2 sm:before:bottom-2 sm:before:w-0.5 sm:before:bg-sky-100">
      {days.map((d) => <Day key={d.day} d={d} />)}
    </ol>
  </div>
);
